import { List, Map as ImmutableMap } from 'immutable';
import { pipe, Option, Array as A } from 'effect';
import { ChatUser, Channel, ChannelContext, ChannelLike } from './types';

// Normalize the Sendbird channel-metadata key-value record into a ChannelContext. Only keeps
// recognized keys and coerces `type` to the allowed union.
const toChannelContext = (raw: Record<string, string>): ChannelContext | null => {
  if (typeof raw.app !== 'string' || !raw.app) return null;
  const ctx: ChannelContext = { app: raw.app };
  if (raw.companyId) ctx.companyId = raw.companyId;
  if (raw.documentId) ctx.documentId = raw.documentId;
  if (raw.type === 'direct' || raw.type === 'group') ctx.type = raw.type;
  // `documentKey` is a document-scoped composite (`<companyId>/<documentId>`); only surface it
  // when both IDs are present so the parsed context can't claim a documentKey for a
  // company-only (or contextless) channel — mirrors how buildChannelContext writes it.
  if (raw.documentKey && ctx.companyId && ctx.documentId) ctx.documentKey = raw.documentKey;
  return ctx;
};

// Parse the legacy `data` JSON string into a context. `data` is written ATOMICALLY inside
// createChannel({ data }) (unlike metadata, which is a separate createMetaData call afterwards),
// so it is present on the channel object the instant it exists — including at the moment the
// current-context filter fires on a freshly created/arrived channel. Malformed/absent `data`
// yields null so the caller falls back to metadata.
const parseDataContext = (data: string | null | undefined): ChannelContext | null => {
  if (!data) return null;
  try {
    const raw = JSON.parse(data) as unknown;
    return raw && typeof raw === 'object' ? toChannelContext(raw as Record<string, string>) : null;
  } catch {
    return null;
  }
};

// Resolve a channel's context, preferring the atomic `data` string over `cachedMetaData`.
//
// Context is DUAL-WRITTEN on create (buildChannelContext): `data` in the createChannel call
// (atomic) and `metadata` in a follow-up createMetaData call (lags). The current-context filter
// fires in that gap for a just-created/just-arrived channel — when `cachedMetaData` is still
// empty but `data` already carries companyId — so reading `data` FIRST makes the filter pass
// immediately, with no remount/re-query needed (DA-19646). Metadata remains the fallback for any
// legacy channel created before the dual-write (empty `data`) and is still what the server-side
// metaDataKeyFilter narrows on.
//
// `cachedMetaData`: the SDK exposes channel metadata via this getter (a flat string map), NOT a
// `metaData` field — reading `metaData` returns undefined. GroupChannelListQuery populates it by
// default (includeMetaData defaults to true); createMetaData() updates it on the instance.
// The legacy `data` shape omits `documentKey`, but the client filter compares companyId/documentId
// directly (documentKey is a server-side-only helper), so `data` alone is sufficient here.
export const resolveChannelContext = (
  channel: Pick<ChannelLike, 'cachedMetaData' | 'data'>,
): ChannelContext | null =>
  pipe(
    Option.fromNullable(parseDataContext(channel.data)),
    Option.orElse(() =>
      pipe(
        Option.fromNullable(channel.cachedMetaData),
        // The SDK types cachedMetaData as `object`; at runtime it is a flat string map, so narrow
        // it here at the boundary before handing it to toChannelContext.
        Option.flatMap((meta) => Option.fromNullable(toChannelContext(meta as Record<string, string>))),
      ),
    ),
    Option.getOrNull,
  );

export const isDirectChannel = (channel: Pick<Channel, 'cachedMetaData' | 'customType'>): boolean => {
  const ctx = resolveChannelContext(channel);
  return ctx?.type === 'direct' || channel.customType === 'direct';
};

// Sendbird channel members are keyed by userGUID (channels are created with
// invitedUserIds = [...userGUIDs, userProfile.id], and userProfile.id === userGUID).
// The Redux participant store, however, is keyed with `userId` = email and carries the
// GUID under `userGUID` — so member lookups must match against `userGUID`, not `userId`.
// (Same convention already used in ChatPanel/UserProfileCard.tsx.)
const findParticipant = (users: List<ChatUser>, memberUserId: string): ChatUser | undefined =>
  users.find((u) => u.get('userGUID') === memberUserId);

export const getChannelAvatar = (channel: Channel, currentUserId: string, users: List<ChatUser>): string | null => {
  const isDirect = isDirectChannel(channel);
  if (!isDirect) return channel.coverUrl;

  const otherUserId = channel.members.find((m) => m.userId !== currentUserId)?.userId;
  if (!otherUserId) return channel.coverUrl;

  const user = findParticipant(users, otherUserId);
  return user?.get('picture') || channel.coverUrl;
};

// Last-resort title when no name can be resolved from the channel, its members, or the
// participant store (e.g. a freshly created channel whose only other member is still in the
// invited/pending state, so channel.members hasn't populated yet). Callers pass a localized
// string; the default keeps the pure helper usable without an intl context. "Group name" is
// used instead of "Unknown" because an un-resolved chat is treated as an unnamed group.
const DEFAULT_CHANNEL_TITLE_FALLBACK = 'Group name';

// Resolve a member's display name: prefer the app's participant store — the authoritative
// name source when the member is present (the store is company-scoped and keyed by userGUID,
// so it can be missing the member) — over Sendbird's `nickname` (frequently empty when the
// backend provisions the Sendbird user without one — the cause of the "unknown" title,
// DA-19440), then fall back to the raw userId.
const getMemberName = (member: { userId: string; nickname: string }, users?: List<ChatUser>): string => {
  const participantName = users ? findParticipant(users, member.userId)?.get('username') : undefined;
  return participantName || member.nickname || member.userId;
};

// A channel carries a real, user-chosen name (not empty, not the Sendbird default placeholder).
const hasExplicitName = (channel: Pick<ChannelLike, 'name'>): boolean =>
  Boolean(channel.name) && channel.name !== 'Group Channel';

export const getChannelTitle = (
  channel: ChannelLike,
  currentUserId: string,
  users?: List<ChatUser>,
  fallback: string = DEFAULT_CHANNEL_TITLE_FALLBACK,
): string => {
  if (hasExplicitName(channel)) {
    return channel.name;
  }

  // Derive from the CURRENT members, not the create-time `direct`/`group` flag: that flag is set
  // once and never rewritten on invite, so a former P2P kept showing only the first counterpart
  // instead of listing everyone when a member was added (DA-19568). channel.name here is empty or
  // the "Group Channel" placeholder (a real name returns above), so it's unused.
  const names = channel.members.filter((m) => m.userId !== currentUserId).map((m) => getMemberName(m, users));
  return names.join(', ') || fallback;
};

// Chat-list search matcher (DA-19469). A channel matches when the query is a substring of
// either the displayed title OR any participant's name EXCEPT the current user's own.
//
// - Matching `getChannelTitle` keeps the matcher aligned with exactly what the row shows —
//   the custom name, or for an unnamed group the joined "A, B" member names, so a query that
//   spans the join separator (e.g. a pasted visible title) still matches. `getChannelTitle`
//   already excludes the current user, so this never self-matches.
// - Matching individual participants (beyond the title) is what keeps a RENAMED group
//   findable by member name — its title is the custom name, so a title-only match would hide
//   it. This is the DA-19469 fix; the current user is excluded so a query never matches every
//   chat via the searcher's own name (the intended scope of DA-19452, which over-corrected to
//   a title-only filter).
//
// Names resolve identically in both paths (participant-store username → Sendbird nickname →
// userId). An empty `fallback` avoids the localized placeholder ("Group name") leaking into
// the match.
export const channelMatchesQuery = (
  channel: ChannelLike,
  currentUserId: string,
  users: List<ChatUser> | undefined,
  query: string,
): boolean => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;

  if (getChannelTitle(channel, currentUserId, users, '').toLowerCase().includes(normalized)) {
    return true;
  }

  return channel.members
    .filter((m) => m.userId !== currentUserId)
    .some((m) => getMemberName(m, users).toLowerCase().includes(normalized));
};

// Build the channel-creation context. Context is DUAL-WRITTEN on channel creation:
//   - `metadata`: a flat key-value record written via channel.createMetaData after creation
//     (the SDK createChannel has no metadata argument). This is the field the frontend READS
//     from (resolveChannelContext) and the field Sendbird can filter on server-side.
//   - `data`: a JSON string passed to createChannel({ data }), preserved for backward
//     compatibility with non-frontend `data` consumers (Platform-API exports, BE jobs,
//     rollback). The frontend NEVER reads it back for context.
// `customType` is set on the channel at create time.
//
// Both `metadata` and `data` are derived from the SAME context object below, so the two
// representations can never diverge. Values are raw strings; keys `companyId`/`documentId`/
// `type` are omitted when absent (mirrors the omit-null-optionals shape).
//
// `documentKey` is a composite `<companyId>/<documentId>` added to `metadata` ONLY when a
// document is in context. It lets the Platform API / SDK filter document-scoped channels with a
// single `metadata_key=documentKey` + `metadata_values=<companyId>/<documentId>` lookup, instead
// of combining two separate equality filters. It is a metadata-only construct and is
// deliberately OMITTED from the `data` string, which mirrors the exact pre-migration legacy
// shape `{ app, companyId?, documentId?, type? }` so legacy `data` parsers see new channels
// identically to old ones.
export const buildChannelContext = (
  app: string,
  companyId?: string | null,
  documentId?: string | null,
  type?: 'direct' | 'group',
): { customType: string; metadata: Record<string, string>; data: string } => {
  // Single source of truth for both representations — the legacy `data` shape.
  const context: ChannelContext = { app };
  if (companyId) context.companyId = companyId;
  if (documentId) context.documentId = documentId;
  if (type) context.type = type;

  // metadata mirrors context and additionally carries the document-scoped composite documentKey.
  const metadata: Record<string, string> = { ...context };
  if (companyId && documentId) metadata.documentKey = `${companyId}/${documentId}`;

  return {
    customType: app,
    metadata,
    // Legacy shape: no documentKey (it is a metadata-only filter helper).
    data: JSON.stringify(context),
  };
};

// ---------------------------------------------------------------------------
// Unread counters derived from the Sendbird channel collection.
//
// While the chat drawer is open the Sendbird WS is connected and a
// GroupChannelCollection holds every channel with a live `unreadMessageCount`
// and its context (companyId/documentId) in `cachedMetaData`. The three chat
// filter counters (All / current company / current document) are pure functions
// of that channel list, so we recompute them from scratch on every collection
// event instead of applying deltas — recomputation is idempotent, so the SDK's
// multiple rapid fires per change all converge on the correct absolute totals
// (no drift, no readDelta guessing).
//
// The shapes returned here match `setUnreadFromChannelsAction`'s payload so the
// existing v4 reducer/selectors consume them unchanged:
//   - unreadCompanies : Immutable.Map<companyId, count>              (sum of channel unreads)
//   - unreadDocuments : Immutable.Map<companyId, Map<documentId, count>>
//         (per-document unread COUNT = sum of unreadMessageCount across all channels
//          carrying that documentId in metadata — supports the numeric current-document
//          counter; presence checks still work via Map.has(documentId))
//   - totalUnreadCount: number                                       (sum across all channels)
// ---------------------------------------------------------------------------

// Minimal structural shape needed to count unread — a Sendbird GroupChannel
// (which carries `unreadMessageCount`) plus the context in `cachedMetaData`.
export type UnreadCountableChannel = Pick<ChannelLike, 'cachedMetaData'> & { unreadMessageCount: number };

export interface ChatUnreadCounters {
  unreadCompanies: ImmutableMap<string, number>;
  unreadDocuments: ImmutableMap<string, ImmutableMap<string, number>>;
  totalUnreadCount: number;
}

export const computeUnreadCounters = (channels: ReadonlyArray<UnreadCountableChannel>): ChatUnreadCounters =>
  pipe(
    channels,
    // Only channels that actually have unread messages contribute to any counter.
    A.filter((channel) => channel.unreadMessageCount > 0),
    A.reduce(
      {
        unreadCompanies: ImmutableMap<string, number>(),
        unreadDocuments: ImmutableMap<string, ImmutableMap<string, number>>(),
        totalUnreadCount: 0,
      } as ChatUnreadCounters,
      (acc, channel) => {
        const ctx = resolveChannelContext(channel);
        // A channel with no companyId contributes to the global total only — it
        // can't be attributed to a company or document filter.
        const total = acc.totalUnreadCount + channel.unreadMessageCount;
        if (!ctx?.companyId) {
          return { ...acc, totalUnreadCount: total };
        }

        const companyId = ctx.companyId;
        const unreadCompanies = acc.unreadCompanies.update(companyId, (n) => (n || 0) + channel.unreadMessageCount);

        // Per-document unread COUNT: sum unreadMessageCount across every channel that
        // carries this documentId (there can be more than one channel per document).
        const documentId = ctx.documentId;
        const unreadDocuments = documentId
          ? acc.unreadDocuments.update(companyId, (docs) =>
              (docs || ImmutableMap<string, number>()).update(documentId, (n) => (n || 0) + channel.unreadMessageCount),
            )
          : acc.unreadDocuments;

        return { unreadCompanies, unreadDocuments, totalUnreadCount: total };
      },
    ),
  );
